import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (will be replaced with actual API call)
    setTimeout(() => {
      console.log('Contact Form Submitted:', formData);
      toast({
        title: "Message Received!",
        description: "Thank you for contacting us. A member of our team will respond shortly.",
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2V8ZW58MHx8fHwxNzcxMjgwMTY4fDA&ixlib=rb-4.1.0&q=85"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Get in touch for wholesale enquiries, export coordination, or product sourcing discussions
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#333333] mb-2">Nigeria Office</h3>
                      <p className="text-gray-600 text-sm">
                        Ogun State, Ota<br />
                        Nigeria
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#333333] mb-2">UK Office</h3>
                      <p className="text-gray-600 text-sm">
                        United Kingdom
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#C8A951] to-[#B8994A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#333333] mb-2">Phone</h3>
                      <a href="tel:+2349038145272" className="text-gray-600 text-sm hover:text-[#1B5E20] transition-colors block">
                        Nigeria: +234 903 814 5272
                      </a>
                      <a href="tel:+447803174494" className="text-gray-600 text-sm hover:text-[#1B5E20] transition-colors block mt-1">
                        UK: +44 7803 174494
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-[#F9F9F9]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#C8A951] to-[#B8994A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#333333] mb-2">Email</h3>
                      <a href="mailto:admin@folalabifarms.com" className="text-gray-600 text-sm hover:text-[#1B5E20] transition-colors break-all block">
                        admin@folalabifarms.com
                      </a>
                      <a href="mailto:info@folalabifarms.com" className="text-gray-600 text-sm hover:text-[#1B5E20] transition-colors break-all block mt-1">
                        info@folalabifarms.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] text-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">Business Hours</h3>
                  <div className="space-y-2 text-sm text-white/90">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>8:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span>9:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#333333] mb-2">Send Us a Message</h2>
                  <p className="text-gray-600 mb-8">
                    Contact us for wholesale enquiries, export discussions, or product sourcing questions. We welcome enquiries from importers, distributors, and businesses looking to purchase African food products.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#333333] font-semibold">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-[#333333] font-semibold">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-[#333333] font-semibold">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                          placeholder="+44 123 456 7890"
                        />
                      </div>

                      <div>
                        <Label htmlFor="subject" className="text-[#333333] font-semibold">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                          placeholder="General Inquiry"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#333333] font-semibold">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2 border-gray-300 focus:border-[#1B5E20] focus:ring-[#1B5E20]"
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-[#1B5E20] hover:bg-[#2E7D32] text-white font-semibold py-6 text-lg transition-all shadow-lg hover:shadow-xl"
                    >
                      {isSubmitting ? 'Sending...' : (
                        <>
                          Send Message
                          <Send className="ml-2" size={20} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Why Contact Us */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: CheckCircle, text: 'Response within 24 hours' },
                  { icon: CheckCircle, text: 'Expert guidance & support' },
                  { icon: CheckCircle, text: 'Custom solutions available' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-[#F9F9F9] rounded-lg">
                    <item.icon className="text-[#1B5E20] flex-shrink-0" size={20} />
                    <span className="text-sm text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
